/**
 * ``useStatusMotion`` — the System Status live-motion controller (Flow L
 * §8/§9/§11; ADR-035 / S6.6).
 *
 * Watches the merged (snapshot⊕SSE) node states + connection totals and derives
 * three transient treatments, honoring ``prefers-reduced-motion`` throughout:
 *
 *   1. **Stoplight transition** — a node whose state changes on a *live* update
 *      (not the initial paint) flashes briefly (:data:`transitioning`). Under
 *      reduced motion this set stays empty — the stoplight still swaps instantly
 *      (shape + colour + ``aria-live``), just without the pulse.
 *   2. **Connection-churn burst** — a connections node whose total jumps by
 *      ≥ :data:`BURST_THRESHOLD` flags a burst (:data:`bursts`; ties to the
 *      ``producer.first_sighting`` compromise signal, §11). The burst *badge* is
 *      a state indicator (shown regardless of motion); only its highlight
 *      animation is motion-gated in the diagram.
 *   3. **Sustained failover** — a node stuck non-healthy for ≥
 *      :data:`SUSTAINED_THRESHOLD_MS` escalates amber→red (:data:`sustained`) and
 *      is marked on the node itself with a non-colour corner marker carrying the
 *      duration (architect, 2026-08-01 — the former top-of-panel banner is gone;
 *      it restated in text what the escalation already showed, while the
 *      escalation itself was colour-only). The marker is state, never motion-gated.
 *
 * ``animate`` (= not-reduced-motion) is passed through so the diagram gates its
 * CSS animation classes (belt-and-braces with Tailwind's ``motion-safe:``).
 *
 * Cross-references:
 *   - Flow L §8/§9/§11; REQ-UI-046 (state not by colour/motion alone); REQ-UI-059.
 *   - ./status-transitions (the pure logic); ./use-reduced-motion.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { RollupState } from "@/system-status/status-model";
import type { NodeRender } from "@/system-status/status-diagram";
import {
  BURST_FLASH_MS,
  BURST_THRESHOLD,
  SUSTAINED_RECHECK_MS,
  TRANSITION_FLASH_MS,
  advanceStateTracker,
  burstNodeIds,
  changedNodeIds,
  sustainedDurations,
  type StateSince,
} from "@/system-status/status-transitions";

/** The live-motion treatments for the current render. */
export interface StatusMotion {
  /** Nodes flashing a stoplight transition (empty under reduced motion). */
  transitioning: ReadonlySet<string>;
  /** Connections nodes with a recent burst (state indicator; badge shows always). */
  bursts: ReadonlySet<string>;
  /**
   * Nodes escalated by a sustained non-healthy state → how long they have been
   * non-healthy, in ms. A Map rather than a Set because the **duration** is the
   * information the escalation exists to convey; membership alone is what the
   * rollup colour already says.
   */
  sustained: ReadonlyMap<string, number>;
  /** Whether CSS animations may play (= not reduced motion). */
  animate: boolean;
}

/** Options for :func:`useStatusMotion`. */
export interface UseStatusMotionOptions {
  renders: ReadonlyMap<string, NodeRender>;
  /** Per connections-node total, for burst detection. */
  connectionTotals: ReadonlyMap<string, number | null>;
  reducedMotion: boolean;
}

/** Derive the live-motion treatments (see :type:`StatusMotion`). */
export function useStatusMotion({
  renders,
  connectionTotals,
  reducedMotion,
}: UseStatusMotionOptions): StatusMotion {
  const states = useMemo(() => {
    const map = new Map<string, RollupState>();
    for (const [id, render] of renders) map.set(id, render.state);
    return map;
  }, [renders]);

  const [transitioning, setTransitioning] = useState<Set<string>>(() => new Set());
  const [bursts, setBursts] = useState<Set<string>>(() => new Set());
  const [sustained, setSustained] = useState<Map<string, number>>(() => new Map());
  const [tick, setTick] = useState(0);

  const prevStates = useRef<Map<string, RollupState> | null>(null);
  const prevTotals = useRef<ReadonlyMap<string, number | null> | null>(null);
  const statesRef = useRef<Map<string, RollupState>>(new Map());
  const tracker = useRef<Map<string, StateSince>>(new Map());
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  /** Add ids to a set, then schedule their removal after ``ttl`` ms. */
  const flash = (
    ids: readonly string[],
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    ttl: number,
  ): void => {
    if (ids.length === 0) return;
    setter((cur) => new Set([...cur, ...ids]));
    const handle = setTimeout(() => {
      setter((cur) => {
        const next = new Set(cur);
        for (const id of ids) next.delete(id);
        return next;
      });
      timers.current.delete(handle);
    }, ttl);
    timers.current.add(handle);
  };

  // Transitions + tracker advance + sustained recompute, on each states change.
  useEffect(() => {
    const now = Date.now();
    if (prevStates.current !== null && !reducedMotion) {
      flash(changedNodeIds(prevStates.current, states), setTransitioning, TRANSITION_FLASH_MS);
    }
    prevStates.current = states;
    statesRef.current = states;
    tracker.current = advanceStateTracker(tracker.current, states, now);
    setSustained(sustainedDurations(tracker.current, now));
  }, [states, reducedMotion]);

  // Connection-burst detection, on each totals change. The burst *badge* is a
  // state indicator (populated regardless of motion); the diagram gates only its
  // highlight animation on ``animate``.
  useEffect(() => {
    if (prevTotals.current !== null) {
      flash(
        burstNodeIds(prevTotals.current, connectionTotals, BURST_THRESHOLD),
        setBursts,
        BURST_FLASH_MS,
      );
    }
    prevTotals.current = connectionTotals;
  }, [connectionTotals]);

  // Periodic re-check so a component stuck non-healthy escalates even with no
  // further state change (the sustained clock keeps ticking).
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), SUSTAINED_RECHECK_MS);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (tick === 0) return;
    const now = Date.now();
    tracker.current = advanceStateTracker(tracker.current, statesRef.current, now);
    setSustained(sustainedDurations(tracker.current, now));
  }, [tick]);

  // Clear any pending flash timers on unmount.
  useEffect(
    () => () => {
      for (const handle of timers.current) clearTimeout(handle);
      timers.current.clear();
    },
    [],
  );

  return { transitioning, bursts, sustained, animate: !reducedMotion };
}
